// Utility functions for shipping service - main data handling is now in ShippingContext
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface ShippingTier {
  id: string;
  name: string;
  description: string;
  cost: number;
  jmPrice?: number | null;
  freeShippingThreshold?: number;
  deliveryTimeMin: number;
  deliveryTimeMax: number;
  processingTimeMin: number;
  processingTimeMax: number;
  active: boolean;
  sortOrder: number;
  maxWeight?: number;
  zones?: string[];
  // Territory restrictions
  excludedCountries?: string[]; // Countries where this tier is NOT available
  includedCountries?: string[]; // Countries where this tier IS available (takes precedence)
}

/**
 * Format delivery timeline for display
 */
export const formatDeliveryTime = (tier: ShippingTier): string => {
  if (tier.deliveryTimeMin === tier.deliveryTimeMax) {
    return `${tier.deliveryTimeMin} business day${tier.deliveryTimeMin !== 1 ? 's' : ''}`;
  }
  return `${tier.deliveryTimeMin}-${tier.deliveryTimeMax} business days`;
};

/**
 * Format processing time for display
 */
export const formatProcessingTime = (tier: ShippingTier): string => {
  if (tier.processingTimeMin === tier.processingTimeMax) {
    return `${tier.processingTimeMin} business day${tier.processingTimeMin !== 1 ? 's' : ''}`;
  }
  return `${tier.processingTimeMin}-${tier.processingTimeMax} business days`;
};

/**
 * Get legacy shipping option type for backward compatibility
 */
export const getLegacyShippingOption = (tierId: string): 'standard' | 'express' => {
  // Map new tier IDs to legacy options for backward compatibility
  switch (tierId) {
    case 'express':
    case 'expedited':
    case 'priority':
      return 'express';
    default:
      return 'standard';
  }
};



/**
 * Check if shipping tier is available for given cart weight and country
 */
export const isShippingTierAvailable = (tier: ShippingTier, cartWeight?: number, country?: string): boolean => {
  if (!tier.active) return false;
  if (tier.maxWeight && cartWeight && cartWeight > tier.maxWeight) return false;
  
  // Territory restrictions
  if (country) {
    // Check included countries first (takes precedence)
    if (tier.includedCountries && tier.includedCountries.length > 0) {
      return tier.includedCountries.includes(country);
    }
    
    // Check excluded countries
    if (tier.excludedCountries && tier.excludedCountries.length > 0) {
      return !tier.excludedCountries.includes(country);
    }
  }
  
  return true;
};

// Cache for express shipping discount percentage
let cachedDiscountPercent: number | null = null;
let lastDiscountFetchTime = 0;
const DISCOUNT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Rush'd config interface for backward compatibility
export interface RushdPricingConfig {
  programActive: boolean;
  memberExpressPriceMode: 'percentage_off' | 'flat_price';
  memberRateValue: number;
}

/**
 * Get express shipping discount percentage from Firebase with caching
 */
export const getExpressShippingDiscountPercent = async (): Promise<number> => {
  const now = Date.now();
  
  // Return cached value if it's still fresh
  if (cachedDiscountPercent !== null && (now - lastDiscountFetchTime) < DISCOUNT_CACHE_DURATION) {
    return cachedDiscountPercent;
  }

  try {
    const configDoc = await getDoc(doc(db, 'shippingConfig', 'main'));
    
    if (configDoc.exists()) {
      const data = configDoc.data();
      const discountPercent = data.expressShippingDiscountPercent || 0;
      
      cachedDiscountPercent = discountPercent;
      lastDiscountFetchTime = now;
      return discountPercent;
    }
    
    // Default to 0% discount if config doesn't exist
    cachedDiscountPercent = 0;
    lastDiscountFetchTime = now;
    return 0;
  } catch (error) {
    console.error('Error fetching express shipping discount percent:', error);
    // Return 0% discount on error to not break functionality
    return cachedDiscountPercent || 0;
  }
};

/**
 * Apply discount percentage to shipping cost and floor the result
 * Conforms to currency conversion logic: USD base -> Math.floor -> currency convert
 */
export const applyShippingDiscount = (originalCostUSD: number, discountPercent: number): number => {
  if (discountPercent <= 0) return originalCostUSD;
  
  const discountMultiplier = 1 - (discountPercent / 100);
  const discountedCostUSD = originalCostUSD * discountMultiplier;
  
  // Floor to nearest whole dollar (as per requirements)
  return Math.floor(discountedCostUSD);
};

/**
 * @deprecated Use useRushdSettings hook directly for real-time settings
 * This function is kept for backward compatibility but uses default values
 */
export const getRushdPricingConfig = async (): Promise<RushdPricingConfig> => {
  console.warn('getRushdPricingConfig is deprecated. Use useRushdSettings hook for real-time settings.');
  
  // Return default config - components should use useRushdSettings hook instead
  const defaultConfig: RushdPricingConfig = {
    programActive: false,
    memberExpressPriceMode: 'percentage_off',
    memberRateValue: 25,
  };
  
  return defaultConfig;
};

/**
 * Calculate Rush'd member rate from DHL rate using configured settings
 */
export const calculateRushdMemberRate = (dhlRateUSD: number, config: RushdPricingConfig): number => {
  if (config.memberExpressPriceMode === 'percentage_off') {
    return applyShippingDiscount(dhlRateUSD, config.memberRateValue);
  } else {
    // Flat price mode
    return config.memberRateValue;
  }
}; 
